/** @jsxImportSource ../i18n */
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, ListChecks, Scale, Users } from 'lucide-react';
import api from '../services/api';
import { MascotAvatar } from '../components/mascotCatalog';
import { CalibrationSection } from '../components/reports/Calibration';
import { formatDate, formatNumber, formatPercent } from '../theme/format';
import {
  Badge, Banner, Button, Card, CardHeader, EmptyState, PageHeader, Progress, SegmentedControl, SkeletonStat, SkeletonTable, SymbolMark, Table, TBody, Td, Th, THead, Tr,
} from '../components/ui';

const HORIZONS = [
  { value: 5, label: '5 séances' },
  { value: 20, label: '20 séances' },
  { value: 60, label: '60 séances' },
];

export const DECISION_LABELS = {
  'action:renforcer': 'Renforcer',
  'action:conserver': 'Conserver',
  'action:alleger': 'Alléger',
  'action:solder': 'Solder',
  'watchlist:high': 'Watchlist · intérêt fort',
  'watchlist:medium': 'Watchlist · intérêt moyen',
  'watchlist:low': 'Watchlist · intérêt faible',
  'watchlist:none': 'Watchlist · aucun intérêt',
};

const VERDICTS = {
  scored: (hit) => (hit ? ['gain', 'Juste'] : ['loss', 'Erronée']),
  pending: () => ['neutral', 'En cours'],
  unscored: () => ['neutral', 'Non notée'],
  no_data: () => ['neutral', 'Sans cours'],
};

const rate = (t) => (t?.hit_rate === null || t?.hit_rate === undefined ? null : t.hit_rate);

const TallyRow = ({ label, tally, leading, note }) => (
  <li className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
    {leading}
    <div className="min-w-0 flex-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium text-fg">{label}</span>
        <span className="num text-sm text-fg">{formatPercent(rate(tally), { sign: false, decimals: 0 })}</span>
      </div>
      <Progress value={rate(tally) || 0} max={100} className="mt-2" />
      <p className="num mt-1 text-xs text-fg-subtle">{tally.hits} {tally.hits > 1 ? 'justes' : 'juste'} sur {tally.scored}{note}</p>
    </div>
  </li>
);

const PanelScorecard = () => {
  const [horizon, setHorizon] = useState(20);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    setData(null);
    try {
      const res = await api.get('/analysis/panel-scorecard', { params: { horizon } });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Impossible de charger le bilan du panel.');
    }
  }, [horizon]);

  useEffect(() => { load(); }, [load]);

  const decisions = data?.decisions || [];
  const byDecision = Object.entries(data?.by_decision || {}).sort(([a], [b]) => Object.keys(DECISION_LABELS).indexOf(a) - Object.keys(DECISION_LABELS).indexOf(b));

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Intelligence"
        title="Bilan du panel"
        description="Chaque décision du panel est confrontée au marché : la valeur a-t-elle fait mieux ou moins bien que le CAC 40 ?"
        actions={<SegmentedControl ariaLabel="Horizon d’évaluation" options={HORIZONS} value={horizon} onChange={setHorizon} />}
      />
      {error && <Banner tone="loss" role="alert" action={<Button size="sm" variant="secondary" onClick={load}>Réessayer</Button>}>{error}</Banner>}
      {!data && !error && (
        <div className="space-y-6" role="status">
          <span className="sr-only">Chargement du bilan…</span>
          <SkeletonStat />
          <Card aria-hidden="true"><SkeletonTable rows={5} cols={5} /></Card>
        </div>
      )}
      {data && decisions.length === 0 && (
        <Card>
          <EmptyState
            icon={Scale}
            title="Pas encore de décision à évaluer"
            description="Le bilan se construit à partir des rapports d’experts : il faut au moins deux experts actifs dans le panel."
            action={<Button as={Link} to="/settings?tab=experts" variant="secondary">Configurer les experts</Button>}
          />
        </Card>
      )}
      {data && decisions.length > 0 && (
        <>
          <Card tone="gold" className="flex flex-col gap-6 sm:flex-row sm:items-center" data-testid="scorecard-hero">
            <div>
              <p className="eyebrow">Décisions justes</p>
              <p className="mt-2 font-display text-display-xl text-fg">
                {rate(data.panel) === null ? '—' : <span className="num">{formatNumber(rate(data.panel), { decimals: 0 })} %</span>}
              </p>
            </div>
            <div className="text-sm leading-relaxed text-fg-muted sm:border-l sm:border-line sm:pl-6">
              <p>
                <span className="text-fg">{data.panel.hits}</span> décisions justes sur <span className="text-fg">{data.panel.scored}</span> notées à {data.horizon} séances, face au {data.benchmark}.
              </p>
              <p className="mt-1">
                {data.pending > 0 && <>{data.pending} décision{data.pending > 1 ? 's' : ''} en cours d’évaluation · </>}
                Depuis le {formatDate(data.since, 'long')}.
              </p>
            </div>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
            <Card>
              <CardHeader icon={ListChecks} title="Par décision" description="Taux de décisions justes selon leur nature." />
              {byDecision.length === 0
                ? <p className="text-sm text-fg-muted">Aucune décision notée pour l’instant.</p>
                : <ul className="divide-y divide-line">{byDecision.map(([key, t]) => <TallyRow key={key} label={DECISION_LABELS[key] || key} tally={t} />)}</ul>}
              {data.sell_regret && (
                <p className="mt-4 rounded-control border border-line bg-surface-2/50 px-3 py-2 text-sm leading-relaxed text-fg-body" data-testid="scorecard-sell-regret">
                  {`Coût des ventes : après ${data.sell_regret.count} allègement${data.sell_regret.count > 1 ? 's' : ''} ou vente${data.sell_regret.count > 1 ? 's' : ''}, la ligne a fait en moyenne ${formatNumber(data.sell_regret.average, { decimals: 1, sign: true })} pt face au ${data.benchmark} (mieux que lui ${data.sell_regret.missed} fois). La plus coûteuse : ${data.sell_regret.worst}, décidée le ${formatDate(data.sell_regret.worst_at, 'long')} (${formatNumber(data.sell_regret.worst_relative, { decimals: 1, sign: true })} pt).`}
                </p>
              )}
            </Card>
            <Card>
              <CardHeader
                icon={Users}
                title="Par expert"
                description={data.horizon === 20
                  ? 'La position de chaque expert, notée comme celle du panel. La voix est le poids de son vote quand le vote pondéré est activé (Réglages › Experts).'
                  : 'La position de chaque expert, notée comme celle du panel.'}
              />
              {data.experts.length === 0
                ? <p className="text-sm text-fg-muted">Aucune position individuelle notée pour l’instant.</p>
                : (
                  <ul className="divide-y divide-line" data-testid="scorecard-experts">
                    {data.experts.map((e, i) => (
                      <TallyRow
                        key={e.name}
                        label={<>{e.name}{i === 0 && rate(e) !== null && <Badge tone="gold" icon={Award} className="ml-2">En tête</Badge>}</>}
                        tally={e}
                        leading={<MascotAvatar variant={e.mascot_variant} name={e.name} size="md" />}
                        note={data.horizon === 20 && e.vote_weight ? <> · voix ×{formatNumber(e.vote_weight, { decimals: 2 })}</> : null}
                      />
                    ))}
                  </ul>
                )}
            </Card>
          </div>

          <Card>
            <CardHeader title="Décisions récentes" description={`Performance de la valeur et du CAC 40 sur ${data.horizon} séances, depuis la clôture précédant le rapport.`} />
            <Table>
              <THead>
                <tr>
                  <Th>Valeur</Th>
                  <Th>Décision</Th>
                  <Th align="right" className="hidden md:table-cell">Valeur / indice</Th>
                  <Th align="right">Écart</Th>
                  <Th align="right">Verdict</Th>
                </tr>
              </THead>
              <TBody>
                {decisions.map((d, i) => {
                  const [tone, label] = VERDICTS[d.status]?.(d.hit) || ['neutral', '—'];
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <Tr key={`${d.run_id}-${d.symbol}-${i}`} data-testid="scorecard-decision">
                      <Td className="w-full max-w-0">
                        <div className="flex items-center gap-3">
                          <SymbolMark symbol={d.symbol} size="sm" />
                          <div className="min-w-0">
                            <p className="truncate font-medium text-fg">{d.symbol}</p>
                            <p className="text-xs text-fg-subtle">{formatDate(d.date)}</p>
                          </div>
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap text-fg-body">{DECISION_LABELS[`${d.kind}:${d.decision}`] || d.decision}</Td>
                      <Td align="right" numeric className="hidden whitespace-nowrap md:table-cell">
                        {d.stock_return === null || d.stock_return === undefined ? '—' : `${formatPercent(d.stock_return)} / ${formatPercent(d.index_return)}`}
                      </Td>
                      <Td align="right" numeric className="whitespace-nowrap">
                        {d.relative === null || d.relative === undefined ? '—' : `${formatNumber(d.relative, { decimals: 1, sign: true })} pts`}
                      </Td>
                      <Td align="right"><Badge tone={tone} dot={d.status === 'scored'}>{label}</Badge></Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
            <p className="mt-5 text-xs leading-relaxed text-fg-subtle">
              Règles : « Renforcer » et un intérêt fort sont justes si la valeur bat le CAC 40 ; « Alléger », « Solder », un intérêt faible ou nul, si elle fait moins bien ; « Conserver », si elle ne décroche pas de plus de 3 points. L’intérêt moyen n’est pas noté. Indice de prix, hors dividendes.
            </p>
          </Card>
        </>
      )}
      {/* Horizon-independent: stays mounted while the horizon changes. */}
      <CalibrationSection />
    </div>
  );
};

export default PanelScorecard;
